import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGraduationCap, FaUser, FaEnvelope, FaPhone, FaCalendar, FaMapMarkerAlt, FaArrowLeft, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import './UniversityCriteria.css';

const UniversityCriteria = () => {
  const navigate = useNavigate();
  const { token, profile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedCriteria, setHasSubmittedCriteria] = useState(false);

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',

    // Educational Background
    highestEducation: '',
    institutionName: '',
    graduationYear: '',
    gpa: '',
    gpaScale: '10.0',
    major: '',

    // Program Preferences
    desiredProgram: '',
    studyLevel: '',
    startDate: '',
    studyMode: '',

    // Additional Information
    workExperience: '',
    skills: '',
    interests: '',
    goals: '',
    howDidYouHear: '',

    // Contact Preferences
    preferredContact: 'email',
    timezone: '',
    language: 'English',
  });

  // Check if user has already submitted criteria
  useEffect(() => {
    const checkCriteriaStatus = async () => {
      if (!token || !profile) return;

      try {
        const response = await axios.get(api.checkUniversityCriteria(profile._id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.hasSubmitted) {
          setHasSubmittedCriteria(true);
        }
      } catch (error) {
        console.error('Error checking criteria status:', error);
      }
    };

    checkCriteriaStatus();
  }, [token, profile]);

  // Pre-fill form with user profile data
  useEffect(() => {
    if (profile) {
      const nameParts = profile.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setFormData(prev => ({
        ...prev,
        firstName: firstName,
        lastName: lastName,
        email: profile.email || '',
        phone: profile.phone || '',
      }));
    }
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    const requiredFields = {
      1: ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender'],
      2: ['highestEducation', 'institutionName', 'graduationYear', 'major'],
      3: ['desiredProgram', 'studyLevel', 'startDate', 'studyMode'],
      4: ['goals', 'howDidYouHear'],
    };

    const fieldsToValidate = requiredFields[currentStep];
    const missingFields = fieldsToValidate.filter(field => {
      const value = formData[field];
      return !value || value.toString().trim() === '';
    });

    if (missingFields.length > 0) {
      const fieldLabels = {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        phone: 'Phone number',
        dateOfBirth: 'Date of birth',
        gender: 'Gender',
        highestEducation: 'Highest education',
        institutionName: 'Institution name',
        graduationYear: 'Graduation year',
        major: 'Major',
        desiredProgram: 'Desired program',
        studyLevel: 'Study level',
        startDate: 'Start date',
        studyMode: 'Study mode',
        goals: 'Goals',
        howDidYouHear: 'How did you hear about us',
      };

      const missingLabels = missingFields.map(field => fieldLabels[field] || field);
      toast.error(`Please fill in all required fields: ${missingLabels.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    // Final validation before submission
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender',
      'highestEducation', 'institutionName', 'graduationYear', 'major',
      'desiredProgram', 'studyLevel', 'startDate', 'studyMode',
      'goals', 'howDidYouHear',
    ];

    const missingFields = requiredFields.filter(field => {
      const value = formData[field];
      return !value || value.toString().trim() === '';
    });

    if (missingFields.length > 0) {
      const fieldLabels = {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        phone: 'Phone number',
        dateOfBirth: 'Date of birth',
        gender: 'Gender',
        highestEducation: 'Highest education',
        institutionName: 'Institution name',
        graduationYear: 'Graduation year',
        major: 'Major',
        desiredProgram: 'Desired program',
        studyLevel: 'Study level',
        startDate: 'Start date',
        studyMode: 'Study mode',
        goals: 'Goals',
        howDidYouHear: 'How did you hear about us',
      };

      const missingLabels = missingFields.map(field => fieldLabels[field] || field);
      toast.error(`Please fill in all required fields: ${missingLabels.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        api.submitUniversityCriteria,
        {
          ...formData,
          userId: profile._id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        toast.success('Criteria submitted successfully!');
        setHasSubmittedCriteria(true);
        navigate('/university-home');
      }
    } catch (error) {
      console.error('Error submitting criteria:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to submit criteria. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGetStarted = () => {
    if (hasSubmittedCriteria) {
      navigate('/university-home');
    } else {
      navigate('/university-criteria');
    }
  };

  const renderStep1 = () => (
    <div className="criteria-step">
      <h3><FaUser /> Personal Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="Enter your first name"
          />
        </div>
        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Enter your last name"
            required
            minLength="1"
          />
          {formData.lastName === '' && (
            <div className="error-message">Last name is required</div>
          )}
        </div>
        <div className="form-group">
          <label>Email *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter your email"
          />
        </div>
        <div className="form-group">
          <label>Phone Number *</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Enter your phone number"
          />
        </div>
        <div className="form-group">
          <label>Date of Birth *</label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label>Gender *</label>
          <select name="gender" value={formData.gender} onChange={handleInputChange}>
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer-not-to-say">Prefer not to say</option>
          </select>
        </div>
        <div className="form-group">
          <label>Nationality</label>
          <input
            type="text"
            name="nationality"
            value={formData.nationality}
            onChange={handleInputChange}
            placeholder="Enter your nationality"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="criteria-step">
      <h3><FaGraduationCap /> Educational Background</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Highest Education Level *</label>
          <select name="highestEducation" value={formData.highestEducation} onChange={handleInputChange}>
            <option value="">Select education level</option>
            <option value="high-school">High School</option>
            <option value="associate">Associate Degree</option>
            <option value="bachelor">Bachelor's Degree</option>
            <option value="master">Master's Degree</option>
            <option value="phd">PhD</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label>Institution Name *</label>
          <input
            type="text"
            name="institutionName"
            value={formData.institutionName}
            onChange={handleInputChange}
            placeholder="Enter institution name"
          />
        </div>
        <div className="form-group">
          <label>Graduation Year *</label>
          <input
            type="number"
            name="graduationYear"
            value={formData.graduationYear}
            onChange={handleInputChange}
            placeholder="e.g., 2023"
            min="1950"
            max="2030"
          />
        </div>
        <div className="form-group">
          <label>Major/Field of Study *</label>
          <input
            type="text"
            name="major"
            value={formData.major}
            onChange={handleInputChange}
            placeholder="Enter your major or field of study"
          />
        </div>
        <div className="form-group">
          <label>GPA (if applicable)</label>
          <div className="gpa-input-group">
            <input
              type="number"
              name="gpa"
              value={formData.gpa}
              onChange={handleInputChange}
              placeholder="e.g., 6.5"
              min="0"
              max="10"
              step="0.1"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="criteria-step">
      <h3><FaGraduationCap /> Program Preferences</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Desired Program *</label>
          <select name="desiredProgram" value={formData.desiredProgram} onChange={handleInputChange}>
            <option value="">Select desired program</option>
            <option value="computer-science">Computer Science</option>
            <option value="business-administration">Business Administration</option>
            <option value="engineering">Engineering</option>
            <option value="arts-humanities">Arts & Humanities</option>
            <option value="health-sciences">Health Sciences</option>
            <option value="social-sciences">Social Sciences</option>
            <option value="natural-sciences">Natural Sciences</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label>Study Level *</label>
          <select name="studyLevel" value={formData.studyLevel} onChange={handleInputChange}>
            <option value="">Select study level</option>
            <option value="undergraduate">Undergraduate</option>
            <option value="graduate">Graduate</option>
            <option value="phd">PhD</option>
            <option value="certificate">Certificate</option>
            <option value="diploma">Diploma</option>
          </select>
        </div>
        <div className="form-group">
          <label>Preferred Start Date *</label>
          <select name="startDate" value={formData.startDate} onChange={handleInputChange}>
            <option value="">Select start date</option>
            <option value="fall-2024">Fall 2024</option>
            <option value="spring-2025">Spring 2025</option>
            <option value="summer-2025">Summer 2025</option>
            <option value="fall-2025">Fall 2025</option>
            <option value="flexible">Flexible</option>
          </select>
        </div>
        <div className="form-group">
          <label>Study Mode *</label>
          <select name="studyMode" value={formData.studyMode} onChange={handleInputChange}>
            <option value="">Select study mode</option>
            <option value="full-time">Full Time</option>
            <option value="part-time">Part Time</option>
            <option value="online">Online</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="criteria-step">
      <h3><FaCheckCircle /> Additional Information</h3>
      <div className="form-grid">
        <div className="form-group full-width">
          <label>Career Goals *</label>
          <textarea
            name="goals"
            value={formData.goals}
            onChange={handleInputChange}
            placeholder="Describe your career goals and how this program will help you achieve them"
            rows="4"
          />
        </div>
        <div className="form-group">
          <label>Work Experience (years)</label>
          <input
            type="number"
            name="workExperience"
            value={formData.workExperience}
            onChange={handleInputChange}
            placeholder="e.g., 2"
            min="0"
          />
        </div>
        <div className="form-group">
          <label>Skills & Certifications</label>
          <textarea
            name="skills"
            value={formData.skills}
            onChange={handleInputChange}
            placeholder="List your relevant skills and certifications"
            rows="3"
          />
        </div>
        <div className="form-group">
          <label>Areas of Interest</label>
          <textarea
            name="interests"
            value={formData.interests}
            onChange={handleInputChange}
            placeholder="Describe your areas of interest"
            rows="3"
          />
        </div>
        <div className="form-group">
          <label>How did you hear about us? *</label>
          <select name="howDidYouHear" value={formData.howDidYouHear} onChange={handleInputChange}>
            <option value="">Select an option</option>
            <option value="social-media">Social Media</option>
            <option value="search-engine">Search Engine</option>
            <option value="friend-recommendation">Friend/Family Recommendation</option>
            <option value="advertisement">Advertisement</option>
            <option value="university-website">University Website</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label>Preferred Contact Method</label>
          <select name="preferredContact" value={formData.preferredContact} onChange={handleInputChange}>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="both">Both</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderProgressBar = () => (
    <div className="uni-progress-container">
      <div className="uni-progress-bar">
        <div
          className="uni-progress-fill"
          style={{ width: `${(currentStep / 4) * 100}%` }}
        ></div>
      </div>
      <div className="progress-text">
                Step {currentStep} of 4
      </div>
    </div>
  );

  return (
    <div className="university-criteria">
      <div className="uni-criteria-container">
        <div className="uni-criteria-header">
          <button className="back-button" onClick={() => navigate('/university')}>
            <FaArrowLeft /> Back to University
          </button>
          <h1>University Application Criteria</h1>
          <p>Please fill out the following information to help us understand your educational goals</p>
        </div>

        {renderProgressBar()}

        <div className="uni-criteria-content">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        <div className="criteria-actions">
          {currentStep > 1 && (
            <button className="btn-secondary" onClick={prevStep}>
              <FaArrowLeft /> Previous
            </button>
          )}

          {currentStep < 4 ? (
            <button className="btn-primary" onClick={nextStep}>
                            Next <FaArrowRight />
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UniversityCriteria; 